import { Footer } from '../components/Footer';
import TestBanner from '../components/TestBanner';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Users, HandHelping, Calendar, Plus, Trash2, Menu, LogOut, User, Search, Settings, Shield, Edit, Eye, Rocket, FileText, Smartphone, Loader2, MessageSquare, Home, Vote, BookOpen, Package, Droplets, BarChart3, Layers3, ClipboardList, BookOpenText, Sparkles, MapPin, Navigation, Send, ChevronDown, ChevronsUpDown, Copy } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import ChangelogModal from '@/components/admin/ChangelogModal';
import AuditoriaTab from '@/components/admin/AuditoriaTab';
import { GestorBatismosReport } from '@/components/BatismosTab';
import GestorMovimentosTab from '@/components/admin/GestorMovimentosTab';
import ImportIgrejasDialog from '@/components/ImportIgrejasDialog';
import { AccessMenuItems } from '@/components/AccessMenuItems';
import { AccessShortcutButton } from '@/components/AccessShortcutButton';
import { formatPhone } from '@/lib/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOVEMENT_COORD_PERMISSION_MODULES = [
  ['familias', 'Famílias'],
  ['missionarios', 'Missionários'],
  ['grupos', 'Grupos'],
  ['presencas', 'Presença'],
];
const MOVEMENT_COORD_PERMISSION_ACTIONS_BY_MODULE = {
  familias: [['visualizar', 'Visualizar'], ['cadastrar', 'Cadastrar'], ['editar', 'Editar'], ['excluir', 'Excluir']],
  missionarios: [['visualizar', 'Visualizar'], ['cadastrar', 'Cadastrar'], ['editar', 'Editar'], ['excluir', 'Excluir']],
  grupos: [['visualizar', 'Visualizar'], ['cadastrar', 'Cadastrar'], ['editar', 'Editar'], ['excluir', 'Excluir']],
  presencas: [['visualizar', 'Visualizar'], ['marcar_presenca', 'Marcar presença']],
};
const MOVEMENT_COORD_PERMISSION_COLUMNS = [['visualizar', 'Ver', 'Visualizar'], ['cadastrar', 'Cad.', 'Cadastrar'], ['editar', 'Edit.', 'Editar'], ['excluir', 'Exc.', 'Excluir'], ['marcar_presenca', 'Pres.', 'Marcar presença']];
const MOVEMENT_COORD_PERMISSION_SHORT_LABELS = Object.fromEntries(MOVEMENT_COORD_PERMISSION_COLUMNS.map(([action, shortLabel]) => [action, shortLabel]));
const buildMovementCoordPermissions = (enabled = false) => Object.fromEntries(MOVEMENT_COORD_PERMISSION_MODULES.map(([module]) => [module, Object.fromEntries(MOVEMENT_COORD_PERMISSION_ACTIONS_BY_MODULE[module].map(([action]) => [action, !!enabled]))]));
const normalizeMovementCoordPermissions = (value, defaultEnabled = true) => {
  const raw = value && typeof value === 'object' ? value : {};
  return Object.fromEntries(MOVEMENT_COORD_PERMISSION_MODULES.map(([module]) => {
    const source = raw[module] && typeof raw[module] === 'object' ? raw[module] : {};
    const visualizar = !!(source.visualizar ?? defaultEnabled);
    return [module, Object.fromEntries(MOVEMENT_COORD_PERMISSION_ACTIONS_BY_MODULE[module].map(([action]) => [action, action === 'visualizar' ? visualizar : (visualizar ? !!(source[action] ?? defaultEnabled) : false)]))];
  }));
};

const normalizeMovementSelection = (value) => Array.isArray(value) ? value.filter(Boolean) : null;

function PermissionCheckMark({ active, label, testId }) {
  return <span className={`inline-flex h-4 items-center justify-center gap-0.5 whitespace-nowrap rounded border px-1 text-[9px] font-black leading-none ${active ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-300'}`} title={label} data-testid={testId}><span aria-hidden="true">{active ? '✓' : '—'}</span><span>{label}</span></span>;
}

function CoordinatorPermissionCell({ coord, module, moduleLabel }) {
  const permissions = normalizeMovementCoordPermissions(coord.movimentos_permissions, !coord.movimentos_permissions);
  const permission = permissions[module] || {};
  const actions = MOVEMENT_COORD_PERMISSION_ACTIONS_BY_MODULE[module] || [];
  return <div className="flex flex-nowrap items-center gap-0.5 whitespace-nowrap" data-testid={`movement-coordinator-permissions-${coord.user_id}-${module}`}>
    {actions.map(([action]) => <PermissionCheckMark key={action} active={!!permission[action]} label={MOVEMENT_COORD_PERMISSION_SHORT_LABELS[action] || action} testId={`movement-coordinator-permission-check-${coord.user_id}-${module}-${action}`} />)}
  </div>;
}

function CoordinatorStatusFilter({ value, onChange, counts = {} }) {
  const items = [['todos', 'Todos'], ['ativos', 'Ativos'], ['inativos', 'Desativados']];
  const current = items.find(([key]) => key === value)?.[1] || 'Todos';
  return <DropdownMenu>
    <DropdownMenuTrigger asChild><Button variant="outline" className="h-9 justify-between bg-slate-50 border-slate-200 text-sm font-medium" data-testid="movement-coordinators-status-filter-trigger"><span>{current}</span><ChevronDown className="ml-2 h-4 w-4 text-slate-400" /></Button></DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52" data-testid="movement-coordinators-status-filter-content">
      {items.map(([key, label]) => <DropdownMenuItem key={key} onClick={() => onChange(key)} className="flex items-center justify-between" data-testid={`movement-coordinators-status-filter-option-${key}`}><span>{label}</span><Badge variant="outline" className="ml-2 bg-slate-50 text-[10px]">{counts[key] || 0}</Badge></DropdownMenuItem>)}
    </DropdownMenuContent>
  </DropdownMenu>;
}

function CoordinatorMovementsCell({ coord, movementOptions = [] }) {
  const selectedIds = normalizeMovementSelection(coord.movimentos_movement_ids);
  if (selectedIds === null) return <Badge variant="outline" className="bg-emerald-50 text-emerald-700" data-testid={`movement-coordinator-movements-${coord.user_id}`}>Todos</Badge>;
  const selectedNames = selectedIds.map(id => movementOptions.find(item => item.movement_id === id)?.nome || id);
  return <div className="flex flex-wrap gap-1" data-testid={`movement-coordinator-movements-${coord.user_id}`} title={selectedNames.join(', ')}>
    <Badge variant="outline" className="bg-slate-50 text-slate-600" data-testid={`movement-coordinator-movements-count-${coord.user_id}`}>{selectedIds.length} selecionado{selectedIds.length === 1 ? '' : 's'}</Badge>
  </div>;
}

function MovementCoordinatorMovementAccess({ movementOptions = [], value, onChange }) {
  const [search, setSearch] = useState('');
  const selectedIds = normalizeMovementSelection(value);
  const customMode = selectedIds !== null;
  const selectedSet = new Set(selectedIds || []);
  const visibleMovements = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return movementOptions;
    return movementOptions.filter(item => `${item.nome || ''} ${(item.igrejas || []).map(ch => ch.name).join(' ')}`.toLowerCase().includes(term));
  }, [movementOptions, search]);
  const toggleMovement = (movementId) => {
    const current = selectedIds || [];
    onChange(current.includes(movementId) ? current.filter(id => id !== movementId) : [...current, movementId]);
  };
  return <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="movement-coordinator-movement-access-section">
    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div><Label>Movimentos permitidos</Label><p className="mt-1 text-xs text-slate-500" data-testid="movement-coordinator-movement-access-description">Define quais movimentos aparecem no Painel de Movimentos deste usuário.</p></div>
      <div className="flex gap-2" data-testid="movement-coordinator-movement-access-mode-buttons">
        <Button type="button" size="sm" variant={!customMode ? 'default' : 'outline'} className={!customMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => onChange(null)} data-testid="movement-coordinator-movements-all-button">Todos</Button>
        <Button type="button" size="sm" variant={customMode ? 'default' : 'outline'} className={customMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => onChange([])} data-testid="movement-coordinator-movements-custom-button">Selecionar</Button>
      </div>
    </div>
    {!customMode ? <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-3 text-sm font-semibold text-emerald-800" data-testid="movement-coordinator-movements-all-message">Este coordenador verá todos os movimentos.</div> : <div className="space-y-3" data-testid="movement-coordinator-movements-custom-panel">
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar movimento..." className="h-9 bg-white pl-9" data-testid="movement-coordinator-movements-search" /></div>
      <div className="max-h-52 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid="movement-coordinator-movements-list">
        {visibleMovements.length === 0 ? <div className="px-3 py-6 text-center text-sm text-slate-400" data-testid="movement-coordinator-movements-empty">Nenhum movimento encontrado.</div> : visibleMovements.map(item => {
          const checked = selectedSet.has(item.movement_id);
          return <button key={item.movement_id} type="button" onClick={() => toggleMovement(item.movement_id)} className={`flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2.5 text-left last:border-b-0 ${checked ? 'bg-emerald-50' : 'bg-white hover:bg-slate-50'}`} data-testid={`movement-coordinator-movement-option-${item.movement_id}`} aria-pressed={checked}>
            <span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900" data-testid={`movement-coordinator-movement-option-name-${item.movement_id}`}>{item.nome}</span><span className="block truncate text-xs text-slate-500" data-testid={`movement-coordinator-movement-option-status-${item.movement_id}`}>{item.status || 'sem status'}</span></span>
            <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${checked ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white text-transparent'}`} data-testid={`movement-coordinator-movement-option-check-${item.movement_id}`}>✓</span>
          </button>;
        })}
      </div>
      <p className="text-xs font-semibold text-slate-500" data-testid="movement-coordinator-movements-selected-count">{selectedIds.length} movimento{selectedIds.length === 1 ? '' : 's'} selecionado{selectedIds.length === 1 ? '' : 's'}</p>
    </div>}
  </div>;
}

function CoordinatorSortButton({ label, sortKey, activeSort, onSort, testId }) {
  const active = activeSort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 text-left font-black uppercase ${active ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} data-testid={testId}>{label}<ChevronsUpDown className="h-3 w-3" /></button>;
}

function compareCoordinatorValues(a, b, dir = 'asc') {
  const av = String(a ?? '').toLowerCase();
  const bv = String(b ?? '').toLowerCase();
  const result = av.localeCompare(bv, 'pt-BR', { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? result : -result;
}

export default function GestorDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [overview, setOverview] = useState(null);
  const [churches, setChurches] = useState([]);
  const [churchDialog, setChurchDialog] = useState(false);
  const [editingChurch, setEditingChurch] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', city: '', state: '' });
  const [saving, setSaving] = useState(false);
  const [searchChurch, setSearchChurch] = useState('');
  const [manageDialog, setManageDialog] = useState(false);
  const [manageChurch, setManageChurch] = useState(null);
  const [manageTab, setManageTab] = useState('geral');
  const [manageForm, setManageForm] = useState({ name: '', city: '', state: '' });
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPhone, setNewAdminPhone] = useState('');
  const [newAdminBirthDate, setNewAdminBirthDate] = useState('');
  const [newAdminPresentationDate, setNewAdminPresentationDate] = useState('');
  const [sendingAdminPasswordId, setSendingAdminPasswordId] = useState(null);
  const [adminTempPasswordTarget, setAdminTempPasswordTarget] = useState(null);
  const [adminDialog, setAdminDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deleteChurchDialog, setDeleteChurchDialog] = useState(false);
  const [deleteChurchTarget, setDeleteChurchTarget] = useState(null);
  const [deleteChurchConfirmName, setDeleteChurchConfirmName] = useState('');
  const [deleteChurchMode, setDeleteChurchMode] = useState('church_only');
  const [deleteAdminDialog, setDeleteAdminDialog] = useState(false);
  const [deleteAdminTarget, setDeleteAdminTarget] = useState(null);
  const [changelogDialog, setChangelogDialog] = useState(false);
  const [churchAuditLogs, setChurchAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [evoOpen, setEvoOpen] = useState(false);
  const [evoUrl, setEvoUrl] = useState('');
  const [evoKey, setEvoKey] = useState('');
  const [evoSaving, setEvoSaving] = useState(false);
  const [evoSaved, setEvoSaved] = useState(false);
  const [savingModule, setSavingModule] = useState(null);
  const [profileDialog, setProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', data_nascimento: '', data_apresentacao: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [churchGroups, setChurchGroups] = useState([]);
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', church_ids: [] });
  const [savingGroup, setSavingGroup] = useState(false);
  const [importChurches, setImportChurches] = useState({ open: false, delimiter: '|', text: '' });
  const [movementCoordinators, setMovementCoordinators] = useState([]);
  const [movementOptions, setMovementOptions] = useState([]);
  const [movementCoordSearch, setMovementCoordSearch] = useState('');
  const [movementCoordStatusFilter, setMovementCoordStatusFilter] = useState('todos');
  const [movementCoordSort, setMovementCoordSort] = useState({ key: 'name', dir: 'asc' });
  const [movementCoordDialog, setMovementCoordDialog] = useState(false);
  const [editingMovementCoord, setEditingMovementCoord] = useState(null);
  const [movementCoordForm, setMovementCoordForm] = useState({ name: '', email: '', phone: '', active: true, movimentos_permissions: buildMovementCoordPermissions(false), movimentos_movement_ids: null });
  const [savingMovementCoord, setSavingMovementCoord] = useState(false);

  const moduleItems = [
    { key: 'perguntas', label: 'Perguntas', description: 'Formulário público e gestão de perguntas.', icon: MessageSquare },
    { key: 'visitas', label: 'Solic. de Visitas', description: 'Solicitação pública e gestão de visitas.', icon: Home },
    { key: 'votacoes', label: 'Votações', description: 'Criação e resposta de votações.', icon: Vote },
    { key: 'reunioes', label: 'Reuniões', description: 'Reuniões com pautas, atas e tarefas.', icon: BookOpen },
    { key: 'whatsapp', label: 'WhatsApp', description: 'Campanhas e integração Evolution API.', icon: Smartphone },
    { key: 'cestas_basicas', label: 'Montagens', description: 'Histórico de cestas básicas, kits de higiene e valores.', icon: Package },
    { key: 'batismos', label: 'Batismos', description: 'Eventos de batismo, registros de batizados e relatório consolidado.', icon: Droplets },
    { key: 'ebi', label: 'EBI', description: 'Histórias, colaboradoras e acompanhamento do Espaço Bíblico Infantil.', icon: BookOpenText },
    { key: 'movimentos', label: 'Movimentos', description: 'Movimentos de visitas com famílias, missionários, grupos e portal do líder.', icon: MapPin },
    { key: 'ebi_ai', label: 'IA nas Histórias EBI', description: 'Libera recursos de análise por IA no módulo de histórias EBI desta igreja.', icon: Sparkles, requires: 'ebi' },
  ];

  const filteredChurches = useMemo(() => {
    const term = searchChurch.toLowerCase().trim();
    return churches.filter(ch => {
      if (!term) return true;
      return `${ch.name || ''} ${ch.city || ''} ${ch.state || ''} ${(ch.groups || []).map(g => g.name).join(' ')}`.toLowerCase().includes(term);
    });
  }, [churches, searchChurch]);

  const movementCoordStatusCounts = useMemo(() => movementCoordinators.reduce((acc, coord) => {
    acc.todos += 1;
    acc[coord.active ? 'ativos' : 'inativos'] += 1;
    return acc;
  }, { todos: 0, ativos: 0, inativos: 0 }), [movementCoordinators]);

  const filteredMovementCoordinators = useMemo(() => {
    const term = movementCoordSearch.toLowerCase().trim();
    const filtered = movementCoordinators.filter(coord => {
      if (movementCoordStatusFilter === 'ativos' && !coord.active) return false;
      if (movementCoordStatusFilter === 'inativos' && coord.active) return false;
      if (!term) return true;
      return `${coord.name || ''} ${coord.email || ''} ${coord.phone || ''}`.toLowerCase().includes(term);
    });
    const valueFor = (coord) => {
      if (movementCoordSort.key === 'status') return coord.active ? 'ativo' : 'inativo';
      if (movementCoordSort.key === 'phone') return coord.phone || '';
      if (movementCoordSort.key === 'email') return coord.email || '';
      return coord.name || '';
    };
    return [...filtered].sort((a, b) => compareCoordinatorValues(valueFor(a), valueFor(b), movementCoordSort.dir));
  }, [movementCoordinators, movementCoordSearch, movementCoordSort, movementCoordStatusFilter]);

  const toggleMovementCoordSort = (key) => setMovementCoordSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const groupedChurchSections = useMemo(() => {
    const sectionMap = new Map();
    filteredChurches.forEach(ch => {
      const groups = ch.groups || [];
      if (groups.length === 0) {
        const key = 'sem-grupo';
        if (!sectionMap.has(key)) sectionMap.set(key, { key, name: 'Sem grupo', churches: [], ungrouped: true });
        sectionMap.get(key).churches.push(ch);
        return;
      }
      groups.forEach(group => {
        const key = group.group_id || group.name || 'grupo';
        if (!sectionMap.has(key)) sectionMap.set(key, { key, name: group.name || 'Grupo', churches: [], ungrouped: false });
        sectionMap.get(key).churches.push(ch);
      });
    });
    return Array.from(sectionMap.values()).sort((a, b) => {
      if (a.ungrouped && !b.ungrouped) return 1;
      if (!a.ungrouped && b.ungrouped) return -1;
      return a.name.localeCompare(b.name, 'pt-BR');
    });
  }, [filteredChurches]);

  const churchImportLine = (church, delimiter = '|') => {
    const admin = (church.admins || []).find(item => item.role === 'admin') || (church.admins || [])[0] || {};
    return [
      church.name || '',
      church.code || '',
      church.city || '',
      church.state || '',
      admin.name || '',
      admin.email || '',
      admin.phone || admin.whatsapp || '',
      admin.data_nascimento || '',
      admin.data_apresentacao || '',
    ].map(value => String(value || '').replace(/[\r\n]+/g, ' ').trim()).join(delimiter);
  };

  const buildChurchImportText = (items = filteredChurches, delimiter = importChurches.delimiter || '|') => {
    const header = ['Nome', 'Código', 'Cidade', 'Estado', 'Nome do Cooperador', 'Email', 'WhatsApp', 'Data de Nascimento', 'Data de Apresentação'].join(delimiter);
    return [header, ...items.map(church => churchImportLine(church, delimiter))].join('\n');
  };

  const copyVisibleChurchesForImport = async () => {
    if (!filteredChurches.length) { toast.error('Nenhuma igreja visível para copiar'); return; }
    const text = buildChurchImportText(filteredChurches, importChurches.delimiter || '|');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${filteredChurches.length} igreja(s) copiadas no formato de importação`);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(`${filteredChurches.length} igreja(s) copiadas no formato de importação`);
    }
  };

  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    }
  };

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const [userR, overR, churchR, groupsR, movementCoordR, movementsR] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: 'include' }),
        fetch(`${API}/gestor/overview`, { credentials: 'include' }),
        fetch(`${API}/churches`, { credentials: 'include' }),
        fetch(`${API}/gestor/church-groups`, { credentials: 'include' }),
        fetch(`${API}/gestor/movimentos-coordenadores`, { credentials: 'include' }),
        fetch(`${API}/gestor/movimentos`, { credentials: 'include' }),
      ]);
      if (!userR.ok) { navigate('/'); return; }
      const userData = await userR.json();
      if (userData.must_change_password) { navigate('/alterar-senha'); return; }
      if (userData.role !== 'gestor') { navigate('/admin'); return; }
      setUser(userData);
      setProfileForm({ name: userData.name || '', email: userData.email || '', phone: userData.phone || userData.whatsapp || '', data_nascimento: userData.data_nascimento || '', data_apresentacao: userData.data_apresentacao || '' });
      if (overR.ok) setOverview(await overR.json());
      if (churchR.ok) setChurches(await churchR.json());
      if (groupsR.ok) setChurchGroups(await groupsR.json());
      if (movementCoordR.ok) setMovementCoordinators(await movementCoordR.json());
      if (movementsR.ok) setMovementOptions(await movementsR.json());
      // Fetch Evolution API global settings
      try {
        const evoR = await fetch(`${API}/evo/global-settings`, { credentials: 'include' });
        if (evoR.ok) { const d = await evoR.json(); setEvoUrl(d.base_url || ''); setEvoKey(d.api_key || ''); }
      } catch (err) { console.warn('Falha ao carregar Evolution API', err); }
    } catch {
      navigate('/');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await fetch(`${API}/gestor/profile`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao atualizar acesso');
      setUser(prev => ({ ...prev, name: data.name, email: data.email, phone: data.phone, whatsapp: data.phone, data_nascimento: data.data_nascimento, data_apresentacao: data.data_apresentacao }));
      toast.success('Meu acesso atualizado');
      setProfileDialog(false);
    } catch (err) { toast.error(err.message); } finally { setSavingProfile(false); }
  };

  const handleLogout = async () => {
    try { await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch (err) { console.warn('Falha ao sair', err); }
    navigate('/');
  };

  const handleSaveEvo = async () => {
    setEvoSaving(true); setEvoSaved(false);
    try {
      const r = await fetch(`${API}/evo/global-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ base_url: evoUrl, api_key: evoKey }),
      });
      if (r.ok) { toast.success('Configurações Evolution API salvas!'); setEvoSaved(true); }
      else toast.error('Erro ao salvar');
    } catch { toast.error('Erro ao salvar'); }
    setEvoSaving(false);
  };

  const openMovementCoordinator = (coord = null) => {
    setEditingMovementCoord(coord);
    setMovementCoordForm(coord ? { name: coord.name || '', email: coord.email || '', phone: coord.phone || '', active: coord.active !== false, movimentos_permissions: normalizeMovementCoordPermissions(coord.movimentos_permissions, !coord.movimentos_permissions), movimentos_movement_ids: normalizeMovementSelection(coord.movimentos_movement_ids) } : { name: '', email: '', phone: '', active: true, movimentos_permissions: buildMovementCoordPermissions(false), movimentos_movement_ids: null });
    setMovementCoordDialog(true);
  };

  const toggleMovementCoordPermission = (module, action, checked) => {
    setMovementCoordForm(prev => {
      const permissions = normalizeMovementCoordPermissions(prev.movimentos_permissions, false);
      const nextModule = { ...(permissions[module] || {}) };
      if (action === 'visualizar') {
        nextModule.visualizar = checked;
        if (!checked) {
          nextModule.cadastrar = false;
          nextModule.editar = false;
          nextModule.excluir = false;
        }
      } else {
        nextModule[action] = checked;
        if (checked) nextModule.visualizar = true;
      }
      return { ...prev, movimentos_permissions: { ...permissions, [module]: nextModule } };
    });
  };

  const saveMovementCoordinator = async () => {
    setSavingMovementCoord(true);
    try {
      const url = `${API}/gestor/movimentos-coordenadores${editingMovementCoord ? `/${editingMovementCoord.user_id}` : ''}`;
      const r = await fetch(url, { method: editingMovementCoord ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...movementCoordForm, movimentos_movement_ids: normalizeMovementSelection(movementCoordForm.movimentos_movement_ids) }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao salvar coordenador');
      toast.success('Coordenador salvo');
      setMovementCoordDialog(false);
      init();
    } catch (e) { toast.error(e.message || 'Erro ao salvar coordenador'); } finally { setSavingMovementCoord(false); }
  };

  const sendMovementCoordinatorPassword = async (coord) => {
    try {
      const r = await fetch(`${API}/gestor/movimentos-coordenadores/${coord.user_id}/send-temp-password`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao enviar senha');
      toast.success(data.message || 'Senha enviada');
      init();
    } catch (e) { toast.error(e.message || 'Erro ao enviar senha'); }
  };

  const copyMovementCoordinatorPassword = async (coord) => {
    try {
      const r = await fetch(`${API}/gestor/movimentos-coordenadores/${coord.user_id}/copy-temp-password`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao copiar senha');
      const copied = await copyTextToClipboard(data.temp_password || '');
      if (!copied) throw new Error('Não foi possível copiar automaticamente');
      toast.success('Senha temporária copiada');
    } catch (e) { toast.error(e.message || 'Erro ao copiar senha'); }
  };

  const deleteMovementCoordinator = async (coord) => {
    if (!window.confirm(`Excluir definitivamente ${coord.name} da lista de coordenadores de movimentos?`)) return;
    try {
      const r = await fetch(`${API}/gestor/movimentos-coordenadores/${coord.user_id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Erro ao excluir coordenador');
      toast.success('Coordenador excluído da lista');
      init();
    } catch (e) { toast.error(e.message || 'Erro ao excluir coordenador'); }
  };

  const openCreate = () => {
    setEditingChurch(null);
    setForm({ name: '', code: '', city: '', state: '' });
    setChurchDialog(true);
  };

  const openGroupDialog = (group = null) => {
    setEditingGroup(group);
    setGroupForm({ name: group?.name || '', church_ids: group?.church_ids || [] });
    setGroupDialog(true);
  };

  const toggleGroupChurch = (churchId) => {
    setGroupForm(prev => ({ ...prev, church_ids: prev.church_ids.includes(churchId) ? prev.church_ids.filter(id => id !== churchId) : [...prev.church_ids, churchId] }));
  };

  const selectAllGroupChurches = () => {
    setGroupForm(prev => ({ ...prev, church_ids: churches.map(ch => ch.church_id) }));
  };

  const clearGroupChurches = () => {
    setGroupForm(prev => ({ ...prev, church_ids: [] }));
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error('Nome do grupo é obrigatório'); return; }
    if (groupForm.church_ids.length === 0) { toast.error('Selecione ao menos uma igreja'); return; }
    setSavingGroup(true);
    try {
      const r = await fetch(`${API}/gestor/church-groups${editingGroup ? `/${editingGroup.group_id}` : ''}`, { method: editingGroup ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(groupForm) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao salvar grupo');
      toast.success(editingGroup ? 'Grupo atualizado' : 'Grupo criado');
      setGroupDialog(false);
      await init();
    } catch (err) { toast.error(err.message); } finally { setSavingGroup(false); }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Excluir o grupo ${group.name}?`)) return;
    try {
      const r = await fetch(`${API}/gestor/church-groups/${group.group_id}`, { method: 'DELETE', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao excluir grupo');
      toast.success('Grupo excluído');
      await init();
    } catch (err) { toast.error(err.message); }
  };

  const openManage = (ch) => {
    setManageChurch(ch);
    setManageForm({ name: ch.name || '', code: ch.code || '', city: ch.city || '', state: ch.state || '' });
    setManageTab('geral');
    setNewAdminEmail('');
    setNewAdminName('');
    setNewAdminPhone('');
    setNewAdminBirthDate('');
    setNewAdminPresentationDate('');
    setChurchAuditLogs([]);
    setManageDialog(true);
  };

  const fetchChurchLogs = async (churchId) => {
    setLoadingLogs(true);
    try {
      const r = await fetch(`${API}/gestor/churches/${churchId}/audit-logs`, { credentials: 'include' });
      if (r.ok) setChurchAuditLogs(await r.json());
    } catch (err) { console.warn('Falha ao carregar auditoria', err); } finally { setLoadingLogs(false); }
  };

  const handleSaveManage = async () => {
    if (!manageForm.name.trim()) { toast.error('Nome da igreja é obrigatório'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/churches/${manageChurch.church_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(manageForm)
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Igreja atualizada!');
      await init();
      const updated = (await (await fetch(`${API}/churches`, { credentials: 'include' })).json()).find(c => c.church_id === manageChurch.church_id);
      if (updated) setManageChurch(updated);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const openAdminDialog = (admin = null) => {
    setEditingAdmin(admin);
    setNewAdminName(admin ? (admin.name || '') : '');
    setNewAdminEmail(admin ? (admin.email || '') : '');
    setNewAdminPhone(admin ? (admin.phone || admin.whatsapp || '') : '');
    setNewAdminBirthDate(admin ? (admin.data_nascimento || '') : '');
    setNewAdminPresentationDate(admin ? (admin.data_apresentacao || '') : '');
    setAdminDialog(true);
  };

  const refreshManageChurch = async () => {
    const churchesR = await fetch(`${API}/churches`, { credentials: 'include' });
    if (churchesR.ok) {
      const data = await churchesR.json();
      setChurches(data);
      const updated = data.find(c => c.church_id === manageChurch.church_id);
      if (updated) setManageChurch(updated);
    }
  };

  const handleSaveAdmin = async () => {
    if (!newAdminName.trim()) { toast.error('Nome completo é obrigatório'); return; }
    if (!newAdminEmail.trim()) { toast.error('Email é obrigatório'); return; }
    if (!newAdminPhone.trim()) { toast.error('Celular é obrigatório'); return; }
    setSaving(true);
    try {
      if (editingAdmin) {
        const r = await fetch(`${API}/churches/${manageChurch.church_id}/admins/${editingAdmin.user_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: newAdminEmail.trim(), name: newAdminName.trim(), phone: newAdminPhone.trim(), data_nascimento: newAdminBirthDate, data_apresentacao: newAdminPresentationDate })
        });
        if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
        toast.success('Administrador atualizado!');
      } else {
        const r = await fetch(`${API}/churches/${manageChurch.church_id}/admins`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: newAdminEmail.trim(), name: newAdminName.trim(), phone: newAdminPhone.trim(), data_nascimento: newAdminBirthDate, data_apresentacao: newAdminPresentationDate })
        });
        if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
        toast.success('Administrador adicionado!');
      }
      setAdminDialog(false);
      await refreshManageChurch();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleSendAdminTempPassword = async (admin) => {
    setSendingAdminPasswordId(admin.user_id);
    try {
      const r = await fetch(`${API}/churches/${manageChurch.church_id}/admins/${admin.user_id}/send-temp-password`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao enviar senha temporária');
      toast.success('Senha temporária enviada por SMS');
      setAdminTempPasswordTarget(null);
      await refreshManageChurch();
    } catch (err) { toast.error(err.message); } finally { setSendingAdminPasswordId(null); }
  };

  const handleToggleModule = async (moduleKey, checked) => {
    if (!manageChurch) return;
    setSavingModule(moduleKey);
    const nextAddons = { ...(manageChurch.addons || {}), [moduleKey]: checked };
    if (moduleKey === 'ebi' && !checked) nextAddons.ebi_ai = false;
    if (moduleKey === 'ebi_ai' && checked && !nextAddons.ebi) {
      toast.error('Ative o módulo EBI antes de liberar IA nas histórias.');
      return;
    }
    try {
      const r = await fetch(`${API}/churches/${manageChurch.church_id}/modules`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules: { [moduleKey]: checked } }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao atualizar módulo');
      setManageChurch(prev => prev ? ({ ...prev, addons: data.addons || nextAddons }) : prev);
      setChurches(prev => prev.map(ch => ch.church_id === manageChurch.church_id ? ({ ...ch, addons: data.addons || nextAddons }) : ch));
      toast.success(checked ? 'Módulo liberado para a igreja' : 'Módulo bloqueado para a igreja');
    } catch (err) { toast.error(err.message); } finally { setSavingModule(null); }
  };

  const handleRemoveAdmin = async (admin) => {
    setDeleteAdminTarget(admin);
    setDeleteAdminDialog(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!deleteAdminTarget) return;
    try {
      const r = await fetch(`${API}/churches/${manageChurch.church_id}/admins/${deleteAdminTarget.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Administrador removido!');
      setDeleteAdminDialog(false);
      setDeleteAdminTarget(null);
      await refreshManageChurch();
    } catch (err) { toast.error(err.message); }
  };

  const handleImpersonateAdmin = async (admin) => {
    try {
      const res = await fetch(`${API}/gestor/admins/${admin.user_id}/impersonate`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erro'); }
      const data = await res.json();
      if (data.original_token) localStorage.setItem('original_gestor_token', data.original_token);
      window.location.assign(`${API}/auth/impersonate?token=${data.token}&redirect=/admin`);
    } catch (err) { toast.error(err.message); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nome da igreja é obrigatório'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/churches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (!r.ok) { let msg = 'Erro ao criar igreja'; try { const e = await r.json(); msg = e.detail || msg; } catch (err) { console.warn('Falha ao ler erro da igreja', err); } throw new Error(msg); }
      toast.success('Igreja criada!');
      setChurchDialog(false);
      init();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (ch) => {
    setDeleteChurchTarget(ch);
    setDeleteChurchConfirmName('');
    setDeleteChurchMode('church_only');
    setDeleteChurchDialog(true);
  };

  const confirmDeleteChurch = async () => {
    if (!deleteChurchTarget) return;
    if (deleteChurchConfirmName !== deleteChurchTarget.name) { toast.error('O nome digitado não confere'); return; }
    try {
      const r = await fetch(`${API}/churches/${deleteChurchTarget.church_id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: deleteChurchMode }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(deleteChurchMode === 'all_data' ? 'Igreja e dados relacionados excluídos!' : 'Igreja excluída!');
      setDeleteChurchDialog(false);
      setManageDialog(false);
      setChurches(prev => prev.filter(c => c.church_id !== deleteChurchTarget.church_id));
      init();
    } catch (err) { toast.error(err.message); }
  };

  if (!user || !overview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TestBanner />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="gestor-title">
                  Painel Gestor
                </h1>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{user.name}</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AccessShortcutButton navigate={navigate} testId="gestor-access-shortcut-button" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="gestor-menu-trigger"><Menu className="w-5 h-5" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <AccessMenuItems user={user} currentKey="gestor" navigate={navigate} />
                  <DropdownMenuItem onClick={() => setProfileDialog(true)} className="cursor-pointer" data-testid="gestor-menu-profile">
                    <User className="w-4 h-4 mr-2" />Meu Acesso
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem onClick={() => setChangelogDialog(true)} className="cursor-pointer" data-testid="menu-changelog-gestor">
                    <Rocket className="w-4 h-4 mr-2" />Atualizações
                  </DropdownMenuItem> */}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="gestor-menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-100">
          <div className="flex gap-0.5 py-2 overflow-visible" data-testid="gestor-tabs">
            <button onClick={() => setActiveSection('dashboard')} data-testid="gestor-tab-dashboard"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeSection === 'dashboard' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <BarChart3 className="w-3.5 h-3.5" />Dashboard
            </button>
            <button onClick={() => setActiveSection('igrejas')} data-testid="gestor-tab-igrejas"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeSection === 'igrejas' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Building2 className="w-3.5 h-3.5" />Igrejas
            </button>
            <button onClick={() => setActiveSection('batismos')} data-testid="gestor-tab-batismos"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeSection === 'batismos' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Droplets className="w-3.5 h-3.5" />Batismos
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button data-testid="gestor-tab-movimentos-menu" className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeSection === 'movimentos' || activeSection === 'movimentos-coordenadores' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}><MapPin className="w-3.5 h-3.5" />Movimentos<ChevronDown className="w-3 h-3" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem onClick={() => setActiveSection('movimentos')} className="cursor-pointer" data-testid="gestor-tab-movimentos-listar"><MapPin className="w-4 h-4 mr-2" />Listar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveSection('movimentos-coordenadores')} className="cursor-pointer" data-testid="gestor-tab-movimentos-coordenadores"><Navigation className="w-4 h-4 mr-2" />Coordenadores</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={() => setActiveSection('configuracoes')} data-testid="gestor-tab-configuracoes"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeSection === 'configuracoes' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Settings className="w-3.5 h-3.5" />Configurações
            </button>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        {activeSection === 'dashboard' && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="gestor-section-dashboard">
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-igrejas">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs text-slate-500">Igrejas</p>
                <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center"><Building2 className="w-3.5 h-3.5 text-emerald-600" /></div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-emerald-600">{overview.total_churches}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-pessoas">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs text-slate-500">Pessoas</p>
                <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center"><Users className="w-3.5 h-3.5 text-orange-500" /></div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-orange-500">{overview.total_pessoas}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-auxiliares">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs text-slate-500">Auxiliares</p>
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center"><HandHelping className="w-3.5 h-3.5 text-blue-600" /></div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{overview.total_auxiliares}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-eventos">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs text-slate-500">Eventos</p>
                <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center"><Calendar className="w-3.5 h-3.5 text-green-600" /></div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{overview.total_events}</p>
            </CardContent>
          </Card>
        </div>}

        {/* Integrações - Evolution API */}
        {activeSection === 'configuracoes' && <Card className="bg-white border-0 shadow-sm mb-6" data-testid="gestor-section-configuracoes">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Integrações</h2>
                  <p className="text-sm text-slate-500">Evolution API — WhatsApp</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEvoOpen(!evoOpen)} data-testid="evo-toggle">
                <Settings className="w-4 h-4 mr-1.5" />{evoOpen ? 'Fechar' : 'Configurar'}
              </Button>
            </div>
          </CardHeader>
          {evoOpen && (
            <CardContent className="space-y-4 border-t border-slate-100 pt-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-slate-600">URL do Servidor Evolution API</Label>
                  <Input value={evoUrl} onChange={e => setEvoUrl(e.target.value)} placeholder="http://seu-servidor.com" className="mt-1" data-testid="evo-base-url" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">API Key Global</Label>
                  <Input type="password" value={evoKey} onChange={e => setEvoKey(e.target.value)} placeholder="Sua API Key" className="mt-1" data-testid="evo-api-key" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSaveEvo} disabled={evoSaving} data-testid="evo-save-global">
                  {evoSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Shield className="w-4 h-4 mr-1.5" />}
                  Salvar
                </Button>
                {evoSaved && <Badge className="bg-green-100 text-green-700 text-xs">Salvo</Badge>}
              </div>
            </CardContent>
          )}
        </Card>}

        {activeSection === 'batismos' && <GestorBatismosReport churches={churches} />}
        {activeSection === 'movimentos' && <GestorMovimentosTab churches={churches} churchGroups={churchGroups} />}
        {activeSection === 'movimentos-coordenadores' && <section data-testid="gestor-section-movement-coordinators"><Card className="border border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 text-[#069668]"><Navigation className="w-5 h-5" /></div><div><h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Coordenadores de Movimentos</h2><p className="text-sm text-slate-500">{filteredMovementCoordinators.length} {filteredMovementCoordinators.length === 1 ? 'registro' : 'registros'}</p></div></div><Button onClick={() => openMovementCoordinator()} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-movement-coordinator-button"><Plus className="w-4 h-4 mr-1" />Novo</Button></div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,7fr)_minmax(170px,3fr)]"><div className="relative min-w-0"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" /><Input value={movementCoordSearch} onChange={(e) => setMovementCoordSearch(e.target.value)} placeholder="Buscar coordenadores..." className="pl-10 h-9 bg-slate-50 border-slate-200" data-testid="movement-coordinators-search" /></div><CoordinatorStatusFilter value={movementCoordStatusFilter} onChange={setMovementCoordStatusFilter} counts={movementCoordStatusCounts} /></div>
          </CardHeader>
          <CardContent>{filteredMovementCoordinators.length === 0 ? <div className="py-10 text-center text-sm text-slate-500" data-testid="movement-coordinators-empty">Nenhum coordenador encontrado.</div> : <div className="space-y-3" data-testid="movement-coordinators-data-table-wrap"><div className="overflow-hidden rounded-lg border border-slate-200 bg-white"><div className="overflow-x-auto"><Table data-testid="movement-coordinators-table" className="min-w-[1280px]"><TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50"><TableHead className="min-w-[170px] whitespace-nowrap"><CoordinatorSortButton label="Coordenador" sortKey="name" activeSort={movementCoordSort} onSort={toggleMovementCoordSort} testId="movement-coordinators-sort-name" /></TableHead><TableHead className="min-w-[210px] whitespace-nowrap"><CoordinatorSortButton label="E-mail" sortKey="email" activeSort={movementCoordSort} onSort={toggleMovementCoordSort} testId="movement-coordinators-sort-email" /></TableHead><TableHead className="min-w-[130px] whitespace-nowrap"><CoordinatorSortButton label="Telefone" sortKey="phone" activeSort={movementCoordSort} onSort={toggleMovementCoordSort} testId="movement-coordinators-sort-phone" /></TableHead><TableHead className="min-w-[96px] whitespace-nowrap"><CoordinatorSortButton label="Status" sortKey="status" activeSort={movementCoordSort} onSort={toggleMovementCoordSort} testId="movement-coordinators-sort-status" /></TableHead><TableHead className="min-w-[130px] whitespace-nowrap">Famílias</TableHead><TableHead className="min-w-[130px] whitespace-nowrap">Missionários</TableHead><TableHead className="min-w-[130px] whitespace-nowrap">Grupos</TableHead><TableHead className="min-w-[96px] whitespace-nowrap">Presença</TableHead><TableHead className="min-w-[120px] whitespace-nowrap">Movimentos</TableHead><TableHead className="sticky right-0 z-20 min-w-[168px] whitespace-nowrap bg-slate-50 text-right shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.55)]">Ações</TableHead></TableRow></TableHeader><TableBody>{filteredMovementCoordinators.map(coord => <TableRow key={coord.user_id} data-testid={`movement-coordinator-${coord.user_id}`}><TableCell className="whitespace-nowrap"><button type="button" onClick={() => openMovementCoordinator(coord)} className="font-semibold text-slate-900 underline-offset-2 hover:underline" data-testid={`movement-coordinator-name-${coord.user_id}`}>{coord.name}</button></TableCell><TableCell className="max-w-[220px] truncate whitespace-nowrap text-sm text-slate-600" title={coord.email} data-testid={`movement-coordinator-email-${coord.user_id}`}>{coord.email}</TableCell><TableCell className="whitespace-nowrap text-sm text-slate-700" data-testid={`movement-coordinator-phone-${coord.user_id}`}>{coord.phone || 'sem celular'}</TableCell><TableCell className="whitespace-nowrap bg-white"><Badge variant="outline" className={`text-xs ${coord.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500'}`} data-testid={`movement-coordinator-status-${coord.user_id}`}>{coord.active ? 'Ativo' : 'Desativado'}</Badge></TableCell><TableCell className="whitespace-nowrap"><CoordinatorPermissionCell coord={coord} module="familias" /></TableCell><TableCell className="whitespace-nowrap"><CoordinatorPermissionCell coord={coord} module="missionarios" /></TableCell><TableCell className="whitespace-nowrap"><CoordinatorPermissionCell coord={coord} module="grupos" /></TableCell><TableCell className="whitespace-nowrap"><CoordinatorPermissionCell coord={coord} module="presencas" /></TableCell><TableCell className="whitespace-nowrap"><CoordinatorMovementsCell coord={coord} movementOptions={movementOptions} /></TableCell><TableCell className="sticky right-0 z-10 whitespace-nowrap bg-white shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.55)]"><div className="flex justify-end gap-1"><Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openMovementCoordinator(coord)} data-testid={`edit-movement-coordinator-${coord.user_id}`} title="Editar"><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50" onClick={() => sendMovementCoordinatorPassword(coord)} data-testid={`send-movement-coordinator-password-${coord.user_id}`} title="Enviar senha por SMS"><Send className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-50 disabled:text-slate-300" onClick={() => copyMovementCoordinatorPassword(coord)} disabled={!coord.temp_password_copy_available} data-testid={`copy-movement-coordinator-password-${coord.user_id}`} title={coord.temp_password_copy_available ? 'Copiar senha temporária gerada' : 'Envie SMS para gerar senha copiável'}><Copy className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => deleteMovementCoordinator(coord)} data-testid={`delete-movement-coordinator-${coord.user_id}`} title="Excluir definitivamente da lista"><Trash2 className="w-4 h-4" /></Button></div></TableCell></TableRow>)}</TableBody></Table></div></div></div>}</CardContent>
        </Card></section>}

        {/* Churches list */}
        {activeSection === 'igrejas' && <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start" data-testid="gestor-section-igrejas">
            <Card className="bg-white border-0 shadow-sm lg:col-span-1 h-fit" data-testid="church-groups-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Layers3 className="w-5 h-5 text-emerald-600" /></div><div><CardTitle className="text-lg" style={{ fontFamily: 'Work Sans, sans-serif' }}>Grupos</CardTitle><p className="text-sm text-slate-500">{churchGroups.length} agrupamentos</p></div></div>
                  <Button size="sm" onClick={() => openGroupDialog()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-church-group-btn"><Plus className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
                {churchGroups.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">Nenhum grupo criado.</div> : churchGroups.map(group => (
                  <div key={group.group_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`church-group-${group.group_id}`}>
                    <div className="flex items-start justify-between gap-2"><div><p className="text-sm font-semibold text-slate-900">{group.name}</p><p className="text-xs text-slate-500">{group.total_churches || group.church_ids?.length || 0} igrejas</p></div><div className="flex gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openGroupDialog(group)} data-testid={`edit-church-group-${group.group_id}`}><Edit className="w-3.5 h-3.5 text-blue-600" /></Button><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteGroup(group)} data-testid={`delete-church-group-${group.group_id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button></div></div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-sm lg:col-span-3 h-fit" data-testid="churches-table-card">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-emerald-600" /></div><div><CardTitle className="text-lg" style={{ fontFamily: 'Work Sans, sans-serif' }}>Igrejas</CardTitle><p className="text-sm text-slate-500">{churches.length} {churches.length === 1 ? 'igreja cadastrada' : 'igrejas cadastradas'}</p></div></div>
                  <div className="flex items-center gap-2">
                    <Button onClick={copyVisibleChurchesForImport} size="sm" variant="outline" data-testid="copy-churches-import-format-button"><Copy className="w-4 h-4 mr-1" />Copiar para importação</Button>
                    <Button onClick={() => setImportChurches(prev => ({ ...prev, open: true }))} size="sm" variant="outline" data-testid="import-churches-button"><ClipboardList className="w-4 h-4 mr-1" />Importar</Button>
                    <Button onClick={openCreate} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-church-btn"><Plus className="w-4 h-4 mr-1" />Nova Igreja</Button>
                  </div>
                </div>
                <div className="relative mt-3"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" /><Input value={searchChurch} onChange={(e) => setSearchChurch(e.target.value)} placeholder="Buscar igreja ou grupo..." className="pl-10 bg-slate-50 border-slate-200" data-testid="search-church-input" /></div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-0" data-testid="churches-list">
                  {groupedChurchSections.map(section => (
                    <div key={section.key} data-testid={`church-group-section-${section.key}`} className="border-b border-slate-100 last:border-b-0">
                      <div className="bg-slate-50 px-3 sm:px-4 py-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Layers3 className={`w-4 h-4 flex-shrink-0 ${section.ungrouped ? 'text-slate-400' : 'text-emerald-600'}`} />
                          <p className="text-xs font-bold uppercase text-slate-600 truncate">{section.name}</p>
                        </div>
                        <Badge variant="outline" className="bg-white text-slate-600 text-xs">{section.churches.length} {section.churches.length === 1 ? 'igreja' : 'igrejas'}</Badge>
                      </div>
                      {section.churches.map(ch => (
                        <div key={`${section.key}-${ch.church_id}`} className="px-3 sm:px-4 py-3 border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`church-${ch.church_id}`}>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs flex-shrink-0">{(ch.name || 'I').charAt(0)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <p className="font-semibold text-sm truncate text-slate-900">{ch.name}</p>
                                {ch.city && <Badge variant="outline" className="text-xs flex-shrink-0">{ch.city}{ch.state ? ` - ${ch.state}` : ''}</Badge>}
                                {(ch.groups || []).length > 1 && ch.groups.map(g => <Badge key={g.group_id} className="bg-emerald-50 text-emerald-700 text-xs flex-shrink-0">{g.name}</Badge>)}
                              </div>
                              <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                                <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{ch.total_pessoas} pessoas</span>
                                <span className="flex items-center gap-1.5"><HandHelping className="w-3.5 h-3.5" />{ch.total_auxiliares} auxiliares</span>
                                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{ch.total_events} eventos</span>
                              </div>
                              <div className="flex sm:hidden items-center gap-2 text-xs text-slate-500 mt-0.5"><span>{ch.total_pessoas} pessoas</span><span>·</span><span>{ch.total_auxiliares} aux.</span><span>·</span><span>{ch.total_events} eventos</span></div>
                            </div>
                            <div className="flex gap-0.5 flex-shrink-0"><Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-100" onClick={() => openManage(ch)} data-testid={`manage-church-${ch.church_id}`}><Settings className="w-4 h-4" /></Button></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {churches.length === 0 && <div className="text-center py-12"><Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nenhuma igreja cadastrada</p></div>}
                  {churches.length > 0 && groupedChurchSections.length === 0 && <div className="text-center py-12"><Search className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nenhuma igreja encontrada</p></div>}
                </div>
              </CardContent>
            </Card>
        </div>}
      </div>

      {/* Create Church Dialog */}
      <Dialog open={churchDialog} onOpenChange={setChurchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Igreja</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome da Igreja *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="church-name-input" />
            </div>
            <div>
              <Label>Código da Igreja</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex: IA-001" data-testid="church-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cidade</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="church-city-input" />
              </div>
              <div>
                <Label>Estado</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} maxLength={2} data-testid="church-state-input" />
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-church-btn">
              {saving ? 'Salvando...' : 'Criar Igreja'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="max-w-2xl" data-testid="church-group-dialog">
          <DialogHeader><DialogTitle>{editingGroup ? 'Editar Grupo' : 'Novo Grupo de Igrejas'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome do grupo *</Label><Input value={groupForm.name} onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Região Norte" data-testid="church-group-name-input" /></div>
            <div>
              <div className="flex items-center justify-between gap-3">
                <Label>Igrejas do grupo *</Label>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={selectAllGroupChurches} data-testid="church-group-select-all-button">Selecionar todos</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={clearGroupChurches} data-testid="church-group-clear-selection-button">Limpar</Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1" data-testid="church-group-selected-count">{groupForm.church_ids.length} de {churches.length} igrejas selecionadas</p>
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-2 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="church-group-churches-list">{churches.map(ch => <label key={ch.church_id} className="flex items-center gap-2 rounded-md p-2 text-sm hover:bg-slate-50"><input type="checkbox" checked={groupForm.church_ids.includes(ch.church_id)} onChange={() => toggleGroupChurch(ch.church_id)} data-testid={`church-group-checkbox-${ch.church_id}`} /><span className="font-medium text-slate-700">{ch.name}</span></label>)}</div>
            </div>
            <Button onClick={saveGroup} disabled={savingGroup} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-church-group-button">{savingGroup ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}{editingGroup ? 'Salvar Grupo' : 'Criar Grupo'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Church Dialog */}
      <Dialog open={manageDialog} onOpenChange={(v) => { setManageDialog(v); if (!v) setManageTab('geral'); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] p-0 sm:p-0 overflow-hidden">
          <div className="flex h-[70vh]">
            {/* Sidebar */}
            <div className="w-12 sm:w-56 bg-slate-50 border-r border-slate-200 p-2 sm:p-4 flex flex-col gap-1 flex-shrink-0">
              <p className="hidden sm:block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">{manageChurch?.name || 'Igreja'}</p>
              <button onClick={() => setManageTab('geral')} className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${manageTab === 'geral' ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`} data-testid="manage-tab-geral">
                <Settings className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Geral</span>
              </button>
              <button onClick={() => setManageTab('admins')} className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${manageTab === 'admins' ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`} data-testid="manage-tab-admins">
                <Shield className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Administradores</span>
              </button>
              <button onClick={() => setManageTab('modulos')} className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${manageTab === 'modulos' ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`} data-testid="manage-tab-modulos">
                <Rocket className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Módulos</span>
              </button>
              <button onClick={() => { setManageTab('acessos'); if (manageChurch) fetchChurchLogs(manageChurch.church_id); }} className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${manageTab === 'acessos' ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`} data-testid="manage-tab-acessos">
                <FileText className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Acessos</span>
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-8 sm:pt-10">
              {manageTab === 'geral' && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Geral</h3>
                  <div>
                    <Label>Nome da Igreja *</Label>
                    <Input value={manageForm.name} onChange={(e) => setManageForm({ ...manageForm, name: e.target.value })} data-testid="manage-church-name" />
                  </div>
                  <div>
                    <Label>Código da Igreja</Label>
                    <Input value={manageForm.code || ''} onChange={(e) => setManageForm({ ...manageForm, code: e.target.value })} placeholder="Ex: IA-001" data-testid="manage-church-code" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Cidade</Label>
                      <Input value={manageForm.city} onChange={(e) => setManageForm({ ...manageForm, city: e.target.value })} data-testid="manage-church-city" />
                    </div>
                    <div>
                      <Label>Estado</Label>
                      <Input value={manageForm.state} onChange={(e) => setManageForm({ ...manageForm, state: e.target.value })} maxLength={2} data-testid="manage-church-state" />
                    </div>
                  </div>
                  <Button onClick={handleSaveManage} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="manage-save-btn">
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                  <div className="border-t border-slate-200 pt-5 mt-5">
                    <h4 className="text-sm font-semibold text-red-600 mb-2">Zona de perigo</h4>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { if (manageChurch) handleDelete(manageChurch); }} data-testid="manage-delete-btn">
                      <Trash2 className="w-4 h-4 mr-1" />Excluir Igreja
                    </Button>
                  </div>
                </div>
              )}
              {manageTab === 'admins' && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Administradores</h3>
                    <Button onClick={() => openAdminDialog()} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="manage-new-admin-btn">
                      <Plus className="w-4 h-4 mr-1" />Novo Administrador
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500">Gerencie os administradores desta igreja.</p>
                  <div className="divide-y divide-slate-100">
                    {manageChurch?.admins?.map((a, i) => (
                      <div key={i} className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{a.name || a.email}</p>
                            <p className="text-xs text-slate-500">{a.email}</p>
                            <p className="text-xs text-slate-400">{a.phone || a.whatsapp || 'sem celular'} · {a.must_change_password ? 'troca pendente' : a.has_password ? 'senha configurada' : 'sem senha'}</p>
                            {(a.data_nascimento || a.data_apresentacao) && <p className="text-xs text-slate-400" data-testid={`manage-admin-dates-${i}`}>{a.data_nascimento ? `Nasc.: ${a.data_nascimento}` : ''}{a.data_nascimento && a.data_apresentacao ? ' · ' : ''}{a.data_apresentacao ? `Apres.: ${a.data_apresentacao}` : ''}</p>}
                          </div>
                          {a.role === 'gestor' && <Badge className="text-xs bg-emerald-100 text-emerald-700">Gestor</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleImpersonateAdmin(a)} data-testid={`manage-impersonate-admin-${i}`} title="Visualizar como este acesso">
                            <Eye className="w-4 h-4 text-amber-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setAdminTempPasswordTarget(a)} disabled={sendingAdminPasswordId === a.user_id} data-testid={`manage-send-admin-temp-password-${i}`} title="Enviar senha temporária por SMS">
                            {sendingAdminPasswordId === a.user_id ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" /> : <Smartphone className="w-4 h-4 text-emerald-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openAdminDialog(a)} data-testid={`manage-edit-admin-${i}`} title="Editar dados do acesso">
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          {a.role !== 'gestor' && (
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveAdmin(a)} data-testid={`manage-remove-admin-${i}`} title="Remover administrador">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!manageChurch?.admins || manageChurch.admins.length === 0) && (
                      <div className="text-center py-8">
                        <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">Nenhum administrador cadastrado</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {manageTab === 'modulos' && (
                <div className="space-y-5" data-testid="manage-modules-panel">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Módulos</h3>
                    <p className="text-sm text-slate-500">Libere ou bloqueie funcionalidades disponíveis para esta igreja no painel Admin e Auxiliar.</p>
                  </div>
                  <div className="space-y-3">
                    {moduleItems.map(item => {
                      const Icon = item.icon;
                      const enabled = !!manageChurch?.addons?.[item.key];
                      const dependencyDisabled = item.requires && !manageChurch?.addons?.[item.requires];
                      return (
                        <div key={item.key} className={`flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 ${dependencyDisabled ? 'opacity-60' : ''}`} data-testid={`gestor-module-${item.key}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                              <p className="text-xs text-slate-500">{item.description}</p>
                              {dependencyDisabled && <p className="text-[11px] text-amber-600 mt-1">Requer EBI ativo</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{enabled ? 'Ativo' : 'Inativo'}</Badge>
                            <Switch checked={enabled} disabled={savingModule === item.key || dependencyDisabled} onCheckedChange={(checked) => handleToggleModule(item.key, !!checked)} data-testid={`gestor-toggle-module-${item.key}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {manageTab === 'acessos' && (
                <div>
                  {loadingLogs ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    </div>
                  ) : (
                    <AuditoriaTab auditLogs={churchAuditLogs} onRefresh={() => fetchChurchLogs(manageChurch.church_id)} noCard />
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Church Confirmation */}
      <Dialog open={deleteChurchDialog} onOpenChange={setDeleteChurchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Igreja</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="delete-church-warning">
              <p className="text-sm text-red-800 font-medium">Escolha como deseja excluir esta igreja.</p>
              <p className="text-sm text-red-700 mt-1">A opção “Apagar todos os dados” remove cadastros, eventos, presenças, escalas, configurações, usuários e registros vinculados à igreja.</p>
            </div>
            <div className="grid gap-2" data-testid="delete-church-mode-options">
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${deleteChurchMode === 'church_only' ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" name="deleteChurchMode" value="church_only" checked={deleteChurchMode === 'church_only'} onChange={() => setDeleteChurchMode('church_only')} className="mt-1" data-testid="delete-church-mode-only" />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Excluir apenas a igreja</span>
                  <span className="block text-xs text-slate-500">Oculta a igreja da lista. Se houver Pessoas cadastradas, o sistema pedirá para escolher a exclusão completa.</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${deleteChurchMode === 'all_data' ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" name="deleteChurchMode" value="all_data" checked={deleteChurchMode === 'all_data'} onChange={() => setDeleteChurchMode('all_data')} className="mt-1" data-testid="delete-church-mode-all-data" />
                <span>
                  <span className="block text-sm font-semibold text-red-700">Apagar todos os dados da igreja</span>
                  <span className="block text-xs text-red-600">Remove permanentemente todos os cadastros e dados relacionados. Esta ação não pode ser desfeita.</span>
                </span>
              </label>
            </div>
            <div>
              <Label>Digite <span className="font-bold">{deleteChurchTarget?.name}</span> para confirmar:</Label>
              <Input value={deleteChurchConfirmName} onChange={(e) => setDeleteChurchConfirmName(e.target.value)} placeholder="Nome da igreja" className="mt-1" data-testid="delete-church-confirm-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteChurchDialog(false)}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" disabled={deleteChurchConfirmName !== deleteChurchTarget?.name} onClick={confirmDeleteChurch} data-testid="delete-church-confirm-btn">
                <Trash2 className="w-4 h-4 mr-1" />Excluir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Admin Confirmation */}
      <Dialog open={deleteAdminDialog} onOpenChange={setDeleteAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover Administrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Tem certeza que deseja remover <span className="font-semibold">{deleteAdminTarget?.name || deleteAdminTarget?.email}</span>? Ele perdera o acesso a esta igreja.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteAdminDialog(false)}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmRemoveAdmin} data-testid="delete-admin-confirm-btn">
                <Trash2 className="w-4 h-4 mr-1" />Remover
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adminTempPasswordTarget} onOpenChange={(open) => !open && setAdminTempPasswordTarget(null)}>
        <DialogContent className="max-w-md" data-testid="gestor-admin-temp-password-confirm-dialog">
          <DialogHeader><DialogTitle>Enviar nova senha temporária?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Uma nova senha temporária de 6 dígitos será enviada por SMS para:</p>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="gestor-admin-temp-password-target-info">
              <p className="font-semibold text-slate-900">{adminTempPasswordTarget?.name || adminTempPasswordTarget?.email}</p>
              <p className="text-slate-500">{adminTempPasswordTarget?.phone || adminTempPasswordTarget?.whatsapp || 'sem celular'}</p>
            </div>
            <p>Ao confirmar, sessões existentes desse acesso serão encerradas e a troca de senha será obrigatória no próximo login.</p>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setAdminTempPasswordTarget(null)} data-testid="gestor-admin-temp-password-cancel">Cancelar</Button>
            <Button onClick={() => handleSendAdminTempPassword(adminTempPasswordTarget)} disabled={!adminTempPasswordTarget || sendingAdminPasswordId === adminTempPasswordTarget?.user_id} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gestor-admin-temp-password-confirm">
              {sendingAdminPasswordId === adminTempPasswordTarget?.user_id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Smartphone className="w-4 h-4 mr-1.5" />}Enviar SMS
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Admin Dialog */}
      <Dialog open={adminDialog} onOpenChange={setAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Editar Administrador' : 'Novo Administrador'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} placeholder="Nome completo" data-testid="admin-dialog-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="email@exemplo.com" type="email" data-testid="admin-dialog-email" />
              <p className="text-xs text-slate-500 mt-1">Este e-mail será o login do administrador.</p>
            </div>
            <div>
              <Label>Celular *</Label>
              <Input value={newAdminPhone} onChange={(e) => setNewAdminPhone(formatPhone(e.target.value))} placeholder="(11) 99999-9999" inputMode="numeric" data-testid="admin-dialog-phone" />
              <p className="text-xs text-slate-500 mt-1">A senha temporária será enviada por SMS para este número.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Data de Nascimento</Label>
                <Input type="date" value={newAdminBirthDate} onChange={(e) => setNewAdminBirthDate(e.target.value)} data-testid="admin-dialog-birth-date" />
              </div>
              <div>
                <Label>Data de Apresentação</Label>
                <Input type="date" value={newAdminPresentationDate} onChange={(e) => setNewAdminPresentationDate(e.target.value)} data-testid="admin-dialog-presentation-date" />
              </div>
            </div>
            <Button onClick={handleSaveAdmin} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="admin-dialog-save-btn">
              {saving ? 'Salvando...' : (editingAdmin ? 'Salvar' : 'Adicionar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={movementCoordDialog} onOpenChange={setMovementCoordDialog}>
        <DialogContent className="max-h-[86vh] max-w-3xl overflow-y-auto" data-testid="movement-coordinator-dialog">
          <DialogHeader><DialogTitle>{editingMovementCoord ? 'Editar Coordenador de Movimentos' : 'Novo Coordenador de Movimentos'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome *</Label><Input value={movementCoordForm.name} onChange={(e) => setMovementCoordForm(prev => ({ ...prev, name: e.target.value }))} data-testid="movement-coordinator-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={movementCoordForm.email} onChange={(e) => setMovementCoordForm(prev => ({ ...prev, email: e.target.value }))} data-testid="movement-coordinator-email-input" /><p className="mt-1 text-xs text-slate-500">Este e-mail será usado no login.</p></div>
            <div><Label>Celular</Label><Input value={movementCoordForm.phone} onChange={(e) => setMovementCoordForm(prev => ({ ...prev, phone: formatPhone(e.target.value) }))} inputMode="numeric" data-testid="movement-coordinator-phone-input" /><p className="mt-1 text-xs text-slate-500">Usado para envio de senha temporária por SMS.</p></div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={movementCoordForm.active} onChange={(e) => setMovementCoordForm(prev => ({ ...prev, active: e.target.checked }))} data-testid="movement-coordinator-active-checkbox" />Ativo</label>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="movement-coordinator-permissions-section">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <Label>Permissões no Painel de Movimentos</Label>
                  <p className="mt-1 text-xs text-slate-500">Visualizar libera o módulo. As ações dependem de visualizar.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setMovementCoordForm(prev => ({ ...prev, movimentos_permissions: buildMovementCoordPermissions(true) }))} data-testid="movement-coordinator-permissions-select-all">Todos</Button>
              </div>
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid="movement-coordinator-permissions-table">
                <div className="grid grid-cols-[118px_repeat(5,minmax(44px,1fr))] bg-slate-100 text-[10px] font-black uppercase text-slate-500">
                  <div className="px-2 py-2">Módulo</div>{MOVEMENT_COORD_PERMISSION_COLUMNS.map(([action, shortLabel, fullLabel]) => <div key={action} className="px-1 py-2 text-center" title={fullLabel}>{shortLabel}</div>)}
                </div>
                {MOVEMENT_COORD_PERMISSION_MODULES.map(([module, label]) => {
                  const permission = normalizeMovementCoordPermissions(movementCoordForm.movimentos_permissions, false)[module] || {};
                  const moduleActions = new Set(MOVEMENT_COORD_PERMISSION_ACTIONS_BY_MODULE[module].map(([action]) => action));
                  return <div key={module} className="grid grid-cols-[118px_repeat(5,minmax(44px,1fr))] items-center border-t border-slate-100 text-sm" data-testid={`movement-coordinator-permission-row-${module}`}>
                    <div className="px-2 py-2 text-xs font-semibold text-slate-800">{label}</div>
                    {MOVEMENT_COORD_PERMISSION_COLUMNS.map(([action, , actionLabel]) => moduleActions.has(action) ? <label key={action} className="flex items-center justify-center px-1 py-2" title={`${actionLabel} ${label}`}>
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600" checked={!!permission[action]} disabled={action !== 'visualizar' && !permission.visualizar} onChange={(e) => toggleMovementCoordPermission(module, action, e.target.checked)} data-testid={`movement-coordinator-permission-${module}-${action}`} />
                    </label> : <div key={action} className="px-1 py-2 text-center text-slate-300" data-testid={`movement-coordinator-permission-${module}-${action}-not-applicable`}>—</div>)}
                  </div>;
                })}
              </div>
            </div>
            <MovementCoordinatorMovementAccess movementOptions={movementOptions} value={movementCoordForm.movimentos_movement_ids} onChange={(movimentos_movement_ids) => setMovementCoordForm(prev => ({ ...prev, movimentos_movement_ids }))} />
            <Button onClick={saveMovementCoordinator} disabled={savingMovementCoord} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="movement-coordinator-save-button">{savingMovementCoord ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={changelogDialog} onOpenChange={setChangelogDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <ChangelogModal />
        </DialogContent>
      </Dialog>

      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Meu Acesso</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input value={profileForm.name} onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))} data-testid="gestor-profile-name" />
            </div>
            <div>
              <Label>E-mail/Login *</Label>
              <Input type="email" value={profileForm.email} onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))} data-testid="gestor-profile-email" />
            </div>
            <div>
              <Label>Celular para recuperação *</Label>
              <Input value={profileForm.phone} onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="(11) 99999-9999" data-testid="gestor-profile-phone" />
              <p className="text-xs text-slate-500 mt-1">Este número será usado para recuperação de senha por SMS.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Data de Nascimento</Label>
                <Input type="date" value={profileForm.data_nascimento} onChange={(e) => setProfileForm(prev => ({ ...prev, data_nascimento: e.target.value }))} data-testid="gestor-profile-birth-date" />
              </div>
              <div>
                <Label>Data de Apresentação</Label>
                <Input type="date" value={profileForm.data_apresentacao} onChange={(e) => setProfileForm(prev => ({ ...prev, data_apresentacao: e.target.value }))} data-testid="gestor-profile-presentation-date" />
              </div>
            </div>
            <Button onClick={handleSaveProfile} disabled={savingProfile || !profileForm.name || !profileForm.email || !profileForm.phone} data-testid="gestor-profile-save" className="w-full bg-emerald-600 hover:bg-emerald-700">
              {savingProfile ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Salvar Meu Acesso
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportIgrejasDialog state={importChurches} setState={setImportChurches} onImported={init} />
      <Footer />
    </div>
  );
}

// End of GestorDashboard
